export class PriorityQueue<T> {
    private heap: T[] = [];
    private errorFn: (item: T) => number;

    constructor(errorFunction: (item: T) => number) {
        this.errorFn = errorFunction;
    }

    enqueue(item: T) {
        this.heap.push(item);
        this.heapifyUp(this.heap.length - 1);
    }

    dequeue(): T | null {
        if (this.heap.length === 0) return null;
        if (this.heap.length === 1) return this.heap.pop() ?? null;

        const max = this.heap[0];

        // Array emptiness already checked so the subsequent pop() operations are safe.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.heap[0] = this.heap.pop()!;
        this.heapifyDown(0);
        return max;
    }

    peek(): T | null {
        return this.heap.length > 0 ? this.heap[0] : null;
    }

    dequeueIfOver(limit: number): T | null {
        const it = this.peek();

        if (it && this.errorFn(it) > limit) {
            return this.dequeue();
        } else {
            return null;
        }
    }

    get size(): number {
        return this.heap.length;
    }

    reprioritize() {
        // Build heap from bottom up - more efficient than individual heapify operations
        for (let i = Math.floor(this.heap.length / 2) - 1; i >= 0; i--) {
            this.heapifyDown(i);
        }
    }

    clear() {
        this.heap.length = 0;
    }

    initialize(items: Iterable<T>) {
        this.heap = [...items];
        this.reprioritize();
    }

    private heapifyUp(index: number) {
        let currentIndex = index;

        while (currentIndex > 0) {
            const parentIndex = Math.floor((currentIndex - 1) / 2);
            const parentError = this.errorFn(this.heap[parentIndex]);
            const currentError = this.errorFn(this.heap[currentIndex]);

            if (currentError > parentError) {
                this.swap(currentIndex, parentIndex);
                currentIndex = parentIndex;
            } else {
                break;
            }
        }
    }

    private heapifyDown(index: number) {
        let currentIndex = index;

        while (true) {
            const leftChild = 2 * currentIndex + 1;
            const rightChild = 2 * currentIndex + 2;
            let largest = currentIndex;

            if (leftChild < this.heap.length && this.errorFn(this.heap[leftChild]) > this.errorFn(this.heap[largest])) {
                largest = leftChild;
            }

            if (
                rightChild < this.heap.length &&
                this.errorFn(this.heap[rightChild]) > this.errorFn(this.heap[largest])
            ) {
                largest = rightChild;
            }

            if (largest !== currentIndex) {
                this.swap(currentIndex, largest);
                currentIndex = largest;
            } else {
                break;
            }
        }
    }

    private swap(a: number, b: number) {
        const temp = this.heap[a];
        this.heap[a] = this.heap[b];
        this.heap[b] = temp;
    }
}
